/**
 * Seeds the Auth Letter Upload modal from auth-on-file J-codes.
 *
 * Also retains {@link buildCandidRpaRequestPayload} for the legacy RPA
 * filling shape; the live upload path uses
 * {@link buildCandidAuthorizationsRequest} instead.
 *
 * @module candidAuthorizationPayload
 */
import {
  AuthLetterUploadFormState,
  CandidRpaRequestEntry,
  CandidRpaRequestPayload,
} from "../../../data-model/candidAuthorizationModel";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";

/** Portal identifier the RPA service uses to route Candid payloads. */
const CANDID_PORTAL_ID = "candid";

/** Unit label used when the user has not picked one. */
const DEFAULT_UNITS = "VISIT";

/**
 * Groups J-codes by authorization number into one form tab each.
 *
 * Authorization-level fields come from the first J-code of each group. Payer,
 * diagnosis codes, and NPI are left blank so the user has to enter them.
 *
 * @param jCodes - Auth-on-file J-code entries eligible for Candid upload
 * @returns One form tab per unique authorization number
 */
export const groupJCodesByAuthorization = (
  jCodes: JCodeDetails[],
): AuthLetterUploadFormState[] => {
  const grouped = new Map<string, AuthLetterUploadFormState>();

  jCodes.forEach((jCode, index) => {
    const authorizationNumber =
      jCode.auth_number ?? `Authorization #${index + 1}`;
    const serviceCode = jCode.j_code?.trim() ?? "";
    const existing = grouped.get(authorizationNumber);

    if (existing) {
      if (serviceCode) existing.serviceCodes.push(serviceCode);
      return;
    }

    grouped.set(authorizationNumber, {
      authorizationNumber,
      payerName: "",
      startDate: jCode.start_date ?? "",
      endDate: jCode.end_date ?? "",
      serviceCodes: serviceCode ? [serviceCode] : [],
      quantity: jCode.num_visits != null ? String(jCode.num_visits) : "",
      units: DEFAULT_UNITS,
      notes: "",
      dx_codes: [],
      npiBillingNumber: "",
      payerId: "",
    });
  });

  return Array.from(grouped.values());
};

/** Context the modal cannot derive from the authorization tabs alone. */
export interface CandidPayloadContext {
  /** PA order identifier (`order.order_id`). */
  orderId: string;
  /** Organisation id from `getOrgIdForFetchExternalWorklist()`. */
  orgId: string;
  /** Patient identifier (`demographics.patient_id ?? order.patient_id`). */
  patientId: string;
  /** Primary coverage payer name; falls back to the first tab's `payerName`. */
  payerName?: string;
}

/**
 * Flattens one authorization tab into one entry per service code, copying the
 * tab's authorization-level dates and shared quantity / units / notes onto each.
 *
 * @param authorization - A single authorization tab from the modal
 * @returns One RPA entry per service code on that tab
 */
const toEntries = (
  authorization: AuthLetterUploadFormState,
): CandidRpaRequestEntry[] =>
  authorization.serviceCodes.map((serviceCode) => ({
    authorization_number: authorization.authorizationNumber,
    service_code: serviceCode,
    quantity: Number(authorization.quantity) || 0,
    start_date: authorization.startDate,
    expiration_date: authorization.endDate,
    units: authorization.units || DEFAULT_UNITS,
    notes: authorization.notes,
  }));

/**
 * Converts the modal's authorization tabs into a `CandidRpaRequestPayload`.
 *
 * @param authorizations - One entry per sidebar tab in the auth letter upload modal.
 * @param context - Order/org/patient identifiers the tabs do not carry.
 * @returns Request body ready to pass to `triggerCandidFillingRpa`.
 */
export const buildCandidRpaRequestPayload = (
  authorizations: AuthLetterUploadFormState[],
  context: CandidPayloadContext,
): CandidRpaRequestPayload => ({
  order_id: context.orderId,
  org_id: context.orgId,
  portal_id: CANDID_PORTAL_ID,
  patient_id: context.patientId,
  payer_name: context.payerName ?? authorizations[0]?.payerName ?? "",
  entries: authorizations.flatMap(toEntries),
});
